import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import uniqBy from 'lodash/uniqBy';

import { ServiceData, ServiceQuery, ServiceStatus } from 'chaika-frontend-types/frontend-cms';

import { OptionType } from '@components/Select';
import { makeOptionType } from '@utils/makeOptionTypes';
import { store } from '@store';
import { queryClient } from '@store/queryClient';
import { serviceQueryOptions } from '@services/queries/serviceQueries';

interface Params {
    serviceQuery?: ServiceQuery;
    onlySelectedOption?: boolean;
    selectedIds: string[];
}

interface Data {
    defaultOptions: OptionType<string>[];
    loadOptions(query: string): Promise<OptionType<string>[]>;
}

export function useServiceOptions({ serviceQuery, onlySelectedOption, selectedIds }: Params): Data {
    const locale = store.authUser.selectedLanguage;
    const filters: ServiceQuery = { statuses: ServiceStatus.Active, ...serviceQuery };

    const { data: services = [] } = useQuery({
        ...serviceQueryOptions.list(filters, locale),
        enabled: !onlySelectedOption,
    });

    const { data: selectedServices = [] } = useQuery(serviceQueryOptions.byIds(selectedIds, locale));

    const debouncedFetch = useMemo(
        () =>
            AwesomeDebouncePromise(
                (params: ServiceQuery) => queryClient.fetchQuery(serviceQueryOptions.list(params, locale)),
                300,
            ),
        [locale],
    );

    const defaultOptions = onlySelectedOption
        ? makeOptionType(selectedServices)
        : makeOptionType(mergeServices(services, selectedServices));

    const loadOptions = async (query: string): Promise<OptionType<string>[]> => {
        const trimmed = query.trim();
        if (!trimmed) {
            return defaultOptions;
        }

        const found = await debouncedFetch({ ...filters, query: trimmed });
        return makeOptionType(found);
    };

    return { defaultOptions, loadOptions };
}

function mergeServices(services: ServiceData[], selectedServices: ServiceData[]): ServiceData[] {
    return uniqBy([...services, ...selectedServices], 'id');
}
